import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import MapView from "@/components/MapView";
import ControlSheet from "@/components/ControlSheet";
import MeetDialog from "@/components/MeetDialog";
import HowItWorks from "@/components/HowItWorks";
import InstallPrompt from "@/components/InstallPrompt";
import ShareDialog from "@/components/ShareDialog";
import SpotDialog from "@/components/SpotDialog";
import PinPicker from "@/components/PinPicker";
import { postLocation, getMembers, getMeetPin, setMeetPin, respondMeet, clearMeetPin, sendPing, getPings } from "@/lib/api";
import { clearSession, cacheState, readCache } from "@/lib/session";
import { haversine, fmtDistance } from "@/lib/geo";
import { precacheArea } from "@/lib/offlineTiles";
import { useInstallPrompt, isStandalone } from "@/lib/pwa";
import { WifiSlash, WifiMedium } from "@phosphor-icons/react";

function getPosition(highAccuracy) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("no geolocation"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      (err) => reject(err),
      { enableHighAccuracy: highAccuracy, timeout: 10000, maximumAge: highAccuracy ? 5000 : 20000 }
    );
  });
}

function buzz() {
  try {
    if (navigator.vibrate) navigator.vibrate([180, 90, 180]);
  } catch (e) {}
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(1180, ctx.currentTime + 0.13);
    g.gain.setValueAtTime(0.25, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    o.start();
    o.stop(ctx.currentTime + 0.4);
  } catch (e) {}
}

export default function Festival({ session, onLeave, onUpdateSession }) {
  const [members, setMembers] = useState([]);
  const [meet, setMeet] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [status, setStatus] = useState(navigator.onLine ? "online" : "offline"); // online | weak | offline
  const [batterySaver, setBatterySaver] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [meetPrompt, setMeetPrompt] = useState(null);
  const { canInstall, install } = useInstallPrompt();
  const [helpOpen, setHelpOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pendingSpot, setPendingSpot] = useState(null); // {lat,lng} awaiting a name

  // After joining, gently nudge to add to home screen (no forced instructions popup).
  useEffect(() => {
    const t = setTimeout(maybeOpenInstall, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maybeOpenInstall = () => {
    if (isStandalone()) return;
    if (localStorage.getItem("ff_install_dismissed")) return;
    setInstallOpen(true);
  };

  const closeHelp = () => setHelpOpen(false);

  const closeInstall = () => {
    localStorage.setItem("ff_install_dismissed", "1");
    setInstallOpen(false);
  };

  const doInstall = async () => {
    await install();
    closeInstall();
  };

  const myPosRef = useRef(null);
  const dismissedMeetRef = useRef(null);

  // Load cached state immediately for instant map + offline fallback
  useEffect(() => {
    const c = readCache();
    if (c) {
      if (c.members) setMembers(c.members);
      if (c.meet) setMeet(c.meet);
      if (c.myPos) { setMyPos(c.myPos); myPosRef.current = c.myPos; }
    }
  }, []);

  const tick = useCallback(async () => {
    // 1) get my position and push it
    try {
      const pos = await getPosition(!batterySaver);
      setMyPos(pos);
      myPosRef.current = pos;
      await postLocation({
        group_code: session.groupCode,
        user_id: session.userId,
        name: session.name,
        lat: pos.lat,
        lng: pos.lng,
        accuracy: pos.accuracy,
        color: session.color,
        emoji: session.emoji,
      });
    } catch (e) {
      // geolocation may fail; keep last known
    }

    // 2) fetch group state (and gauge connection quality)
    try {
      const started = Date.now();
      const [mem, mp] = await Promise.all([
        getMembers(session.groupCode),
        getMeetPin(session.groupCode),
      ]);
      const elapsed = Date.now() - started;
      setMembers(mem);
      const pin = mp && mp.lat != null ? mp : null;
      setMeet(pin);
      const conn = navigator.connection && navigator.connection.effectiveType;
      const slowConn = conn === "slow-2g" || conn === "2g";
      setStatus(elapsed > 4500 || slowConn ? "weak" : "online");
      cacheState({ members: mem, meet: pin, myPos: myPosRef.current });
    } catch (e) {
      setStatus("offline");
    }
  }, [batterySaver, session]);

  // Lightweight buzz-check, decoupled from location polling so nudges arrive fast.
  const pollPings = useCallback(async () => {
    try {
      const pings = await getPings(session.groupCode, session.userId);
      if (pings && pings.length) {
        buzz();
        const names = [...new Set(pings.map((p) => p.from_name))];
        toast(`${names.join(", ")} ${names.length > 1 ? "are" : "is"} looking for you`, {
          description: "You've been buzzed — glance at the map.",
        });
      }
    } catch (e) {}
  }, [session]);

  // polling loop
  useEffect(() => {
    let cancelled = false;
    const run = () => { if (!cancelled) tick(); };
    run();
    const ms = batterySaver ? 30000 : 12000;
    const id = setInterval(run, ms);
    const onNet = () => setStatus(navigator.onLine ? "online" : "offline");
    window.addEventListener("online", onNet);
    window.addEventListener("offline", onNet);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("online", onNet);
      window.removeEventListener("offline", onNet);
    };
  }, [tick, batterySaver]);

  // ping loop — always brisk (5s) even in battery saver, payload is tiny
  useEffect(() => {
    let cancelled = false;
    const run = () => { if (!cancelled) pollPings(); };
    run();
    const id = setInterval(run, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pollPings]);

  // meet prompt: show when a pin exists that I haven't created or responded to
  useEffect(() => {
    if (!meet || meet.lat == null) { setMeetPrompt(null); return; }
    const mine = meet.created_by === session.userId;
    const responded = meet.responses && meet.responses[session.userId];
    const key = `${meet.lat},${meet.lng},${meet.created_at}`;
    if (!mine && !responded && dismissedMeetRef.current !== key) {
      setMeetPrompt(meet);
    } else {
      setMeetPrompt(null);
    }
  }, [meet, session.userId]);

  const handlePlaceMeet = (latlng) => {
    if (!placing) return;
    setPlacing(false);
    setPendingSpot(latlng); // ask for a name before creating
  };

  const confirmSpot = async (label) => {
    const spot = pendingSpot;
    setPendingSpot(null);
    if (!spot) return;
    try {
      await setMeetPin({
        group_code: session.groupCode,
        user_id: session.userId,
        name: session.name,
        lat: spot.lat,
        lng: spot.lng,
        label: label || "Meet Here",
      });
      toast.success(`"${label || "Meet Here"}" dropped`, { description: "Your crew will be asked to join." });
      tick();
    } catch {
      toast.error("Couldn't drop pin — no service");
    }
  };

  const handleClearMeet = async () => {
    try {
      await clearMeetPin(session.groupCode);
      setMeet(null);
      toast("Meet point removed");
    } catch {
      toast.error("No service");
    }
  };

  const respond = async (response) => {
    const key = meetPrompt ? `${meetPrompt.lat},${meetPrompt.lng},${meetPrompt.created_at}` : null;
    dismissedMeetRef.current = key;
    setMeetPrompt(null);
    try {
      await respondMeet({
        group_code: session.groupCode,
        user_id: session.userId,
        name: session.name,
        response,
      });
      toast.success(response === "accept" ? "See you there!" : "Maybe next time");
      tick();
    } catch {
      toast.error("No service — try again");
    }
  };

  const doLeave = () => {
    clearSession();
    onLeave();
  };

  const pingFriend = async (member) => {
    try {
      await sendPing({
        group_code: session.groupCode,
        from_user_id: session.userId,
        from_name: session.name,
        to_user_id: member.user_id,
      });
      toast.success(`Buzzed ${member.name}`, { description: "Their phone will buzz to check the map." });
    } catch {
      toast.error("No service — couldn't buzz");
    }
  };

  const saveOffline = async () => {
    if (!myPos) return toast.error("Waiting for your location…");
    try {
      const n = await precacheArea(myPos);
      toast.success("Map area saved for offline", { description: `Cached ~${n} tiles around you.` });
    } catch {
      toast.error("Couldn't save map area");
    }
  };

  const savePin = ({ color, emoji }) => {
    setPinOpen(false);
    onUpdateSession({ color, emoji }); // triggers a fresh tick via the polling effect (tick depends on session)
    toast.success("Pin updated");
  };

  const promptDistance = meetPrompt && myPos
    ? fmtDistance(haversine(myPos, { lat: meetPrompt.lat, lng: meetPrompt.lng }))
    : null;

  return (
    <div className="fixed inset-0 h-screen w-screen">
      {status === "offline" && (
        <div data-testid="offline-banner" className="fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-2 border-b-2 border-black bg-destructive py-2 font-mono2 text-xs font-bold uppercase tracking-widest text-white">
          <WifiSlash weight="bold" size={16} /> No Service · Showing Last Known Spots
        </div>
      )}
      {status === "weak" && (
        <div data-testid="weak-banner" className="fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-2 border-b-2 border-black bg-primary py-2 font-mono2 text-xs font-bold uppercase tracking-widest text-black">
          <WifiMedium weight="bold" size={16} /> Weak Signal · Your Last Spot Is Still Shared
        </div>
      )}

      {placing && (
        <div className="fixed inset-x-0 top-0 z-[55] flex items-center justify-center border-b-2 border-black bg-accent py-2 font-mono2 text-xs font-bold uppercase tracking-widest text-white">
          Tap the map to drop your meet point
        </div>
      )}

      <MapView
        myPos={myPos}
        members={members}
        meet={meet}
        myUserId={session.userId}
        recenterTrigger={recenterTrigger}
        onMapClick={handlePlaceMeet}
        onFriendClick={pingFriend}
      />

      <ControlSheet
        session={session}
        members={members}
        myPos={myPos}
        meet={meet}
        batterySaver={batterySaver}
        setBatterySaver={setBatterySaver}
        placing={placing}
        onStartPlace={() => { setPlacing(true); toast("Tap the map where you want to meet"); }}
        onCancelPlace={() => setPlacing(false)}
        onClearMeet={handleClearMeet}
        onRecenter={() => setRecenterTrigger((t) => t + 1)}
        onLeave={doLeave}
        onPingFriend={pingFriend}
        onSaveOffline={saveOffline}
        canInstall={canInstall}
        onInstall={() => setInstallOpen(true)}
        onOpenHelp={() => setHelpOpen(true)}
        onShare={() => setShareOpen(true)}
        onOpenPinPicker={() => setPinOpen(true)}
      />

      <MeetDialog
        meet={meetPrompt}
        distance={promptDistance}
        onAccept={() => respond("accept")}
        onDecline={() => respond("decline")}
      />

      <HowItWorks open={helpOpen} onClose={closeHelp} />
      <InstallPrompt open={installOpen} canInstall={canInstall} onInstall={doInstall} onClose={closeInstall} />
      <ShareDialog open={shareOpen} groupCode={session.groupCode} onClose={() => setShareOpen(false)} />
      <SpotDialog open={!!pendingSpot} onConfirm={confirmSpot} onCancel={() => setPendingSpot(null)} />
      <PinPicker
        open={pinOpen}
        color={session.color}
        emoji={session.emoji}
        name={session.name}
        onSave={savePin}
        onClose={() => setPinOpen(false)}
      />
    </div>
  );
}
